import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { toIsoUtc } from "../chiefComplaints/dateHelpers";
import MedicineService from "./MedicineService";
import { MED_V2_REFRESH } from "./events";

/**
 * Adherence-review sheet — appended to the LATEST StatusEntry of a medicine.
 *
 * Purely a followup audit note (does NOT change medicine.status). A single
 * medicine can accumulate N reviews over time — each Save creates a new
 * entry, never edits an existing one, so the dialog is always launched
 * blank (dedup on the rail is off for this mode; see MedicinesEditor).
 *
 * Fields:
 *   • outcome — 3-state (Yes, completed / Partially / Stopped early)
 *   • adherenceStart + adherenceEnd — the window the reviewer observed;
 *     pre-seeded from the parent StatusEntry's medStartDate + medEndDate
 *     so the common case is one-click ("yes they finished the course").
 *   • description — free-text outcome notes (250-word cap).
 */

const MAX_DESCRIPTION_WORDS = 250;

const countWords = (s) => {
  const t = (s || "").trim();
  return t ? t.split(/\s+/).length : 0;
};
const clampWords = (next, max) => {
  if (countWords(next) <= max) return next;
  const parts = next.split(/(\s+)/);
  let words = 0;
  const out = [];
  for (const chunk of parts) {
    if (/^\s+$/.test(chunk)) { out.push(chunk); continue; }
    if (chunk === "") continue;
    if (words >= max) break;
    out.push(chunk);
    words += 1;
  }
  return out.join("").replace(/\s+$/, "");
};

const CollapseChevron = () => (
  <svg viewBox="0 0 11 11" width="16" height="16" fill="none" aria-hidden="true">
    <path d="M2 7.5L5.5 3.5L9 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
    <path d="M13.3333 2H12.6667V0.666667H11.3333V2H4.66667V0.666667H3.33333V2H2.66667C1.93333 2 1.33333 2.6 1.33333 3.33333V14C1.33333 14.7333 1.93333 15.3333 2.66667 15.3333H13.3333C14.0667 15.3333 14.6667 14.7333 14.6667 14V3.33333C14.6667 2.6 14.0667 2 13.3333 2ZM13.3333 14H2.66667V6.66667H13.3333V14ZM13.3333 5.33333H2.66667V3.33333H13.3333V5.33333Z" fill="#26284D" />
  </svg>
);

const OUTCOMES = [
  { key: "completed", label: "Yes, completed" },
  { key: "partially", label: "Partially" },
  { key: "stopped_early", label: "Stopped early" },
];

// "05 May 2026" — matches the subtitle in the mock.
const shortDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
const isoDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const MedicineAdherenceForm = ({
  patientId,
  formId,
  editing,
  ordinal,
  showOrdinal,
  blockedBySibling,
  onFilledChange,
  onSavingChange,
  onClose,
}) => {
  const medicine = editing;
  const cur = (medicine && medicine.current) || {};

  const [outcome, setOutcome] = useState("");
  // Seed the adherence window from the current segment's course dates so the
  // one-click "Yes, completed as prescribed" case just works.
  const [start, setStart] = useState(isoDate(cur.medStartDate));
  const [end, setEnd] = useState(isoDate(cur.medEndDate));
  const [description, setDescription] = useState("");

  const [collapsed, setCollapsed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // The rail's blank-sheet guard: a review sheet is never blank.
  useEffect(() => {
    onFilledChange(formId, true);
  }, [formId, onFilledChange]);

  const endBeforeStart = start && end && end < start;
  // Guard against a manually-typed pre-1900 date sneaking past the
  // native picker's min on either boundary.
  const startInvalid = !!start && start < "1900-01-01";
  const endInvalid = !!end && end < "1900-01-01";
  const anyPre1900 = startInvalid || endInvalid;
  const canSubmit =
    !!outcome &&
    !!start &&
    !!end &&
    !endBeforeStart &&
    !anyPre1900 &&
    !submitting &&
    !blockedBySibling;

  const close = () => onClose(formId);

  const submit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setFeedback(null);
    onSavingChange(formId, true);

    const svc = new MedicineService();
    const done = () => {
      if (!mounted.current) return;
      onSavingChange(formId, false);
      setSubmitting(false);
      EventService.emit(MED_V2_REFRESH);
      onClose(formId);
    };
    const failed = (err) => {
      if (!mounted.current) return;
      onSavingChange(formId, false);
      setSubmitting(false);
      if (err && err.reason === "version") EventService.emit(MED_V2_REFRESH);
      setFeedback({
        kind: "err",
        text: (err && err.message) || "Save failed — please retry",
      });
    };
    const body = {
      outcome,
      adherenceStart: toIsoUtc(start),
      adherenceEnd: toIsoUtc(end),
      description: description.trim(),
    };
    svc.list(
      patientId,
      (doc) => {
        svc.addAdherenceReview(
          patientId,
          medicine.medicineId,
          { ...body, expectedVersion: (doc && doc.version) || 0 },
          done,
          failed
        );
      },
      failed
    );
  };

  const foldId = `med3-afold-${formId}`;
  const startId = `med3-astart-${formId}`;
  const endId = `med3-aend-${formId}`;
  const descId = `med3-adesc-${formId}`;

  const subtitleRange = cur.medStartDate && cur.medEndDate
    ? `${shortDate(cur.medStartDate)} – ${shortDate(cur.medEndDate)}`
    : cur.medStartDate
    ? `${shortDate(cur.medStartDate)} – Ongoing`
    : "";

  return (
    <section className={`med3-dlg${collapsed ? " is-collapsed" : ""}`}>
      <div className="med3-lbl-row med3-handle">
        <span className="med3-lbl med3-update-title">
          {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
          Review medicine
          <span className="med3-update-sub">
            &middot; {medicine.medicine}
            {(medicine.formulationName || medicine.formulation) && (
              <> ({medicine.formulationName || medicine.formulation})</>
            )}
          </span>
        </span>
        <button
          type="button"
          className="med3-chev"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls={foldId}
          aria-label={collapsed ? "Expand review" : "Collapse review"}
        >
          <CollapseChevron />
        </button>
      </div>

      <div className="med3-fold" id={foldId}>
        <div className="med3-fold-inner">
          {subtitleRange && (
            <p className="med3-adherence-sub">
              Started from {subtitleRange}
            </p>
          )}

          {/* ── Outcome — 3 chips, single-select ── */}
          <div className="med3-field">
            <span className="med3-lbl">
              Did the patient complete the course? <span className="cc3-req">*</span>
            </span>
            <div className="med3-chips" role="radiogroup" aria-label="Adherence outcome">
              {OUTCOMES.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  className={`med3-chip${outcome === o.key ? " is-on" : ""}`}
                  role="radio"
                  aria-checked={outcome === o.key}
                  onClick={() => setOutcome(outcome === o.key ? "" : o.key)}
                  disabled={submitting}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Adherence period ── */}
          <div className="med3-field">
            <span className="med3-lbl">
              Adherence period <span className="cc3-req">*</span>
            </span>
            <div className="med3-two">
              <div className="med3-field">
                <label className="med3-lbl is-muted" htmlFor={startId}>Start date</label>
                <span className="med3-date">
                  <input
                    id={startId}
                    className="med3-date-input"
                    type="date"
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                    min="1900-01-01"
                    disabled={submitting}
                  />
                  <span className="med3-date-icon" aria-hidden="true"><CalendarIcon /></span>
                </span>
              </div>
              <div className="med3-field">
                <label className="med3-lbl is-muted" htmlFor={endId}>End date</label>
                <span className="med3-date">
                  <input
                    id={endId}
                    className="med3-date-input"
                    type="date"
                    value={end}
                    onChange={(e) => setEnd(e.target.value)}
                    min="1900-01-01"
                    disabled={submitting}
                  />
                  <span className="med3-date-icon" aria-hidden="true"><CalendarIcon /></span>
                </span>
              </div>
            </div>
            {endBeforeStart && (
              <p className="fam-field-error">End date cannot precede Start date.</p>
            )}
            {anyPre1900 && (
              <p className="fam-field-error">Dates must be on or after 1 Jan 1900.</p>
            )}
          </div>

          <div className="med3-field">
            <label className="med3-lbl med3-desc-lbl" htmlFor={descId}>
              Description <span className="med3-lbl-hint">&middot; optional</span>
              <span className="med3-words">
                {description
                  ? `${countWords(description)} / ${MAX_DESCRIPTION_WORDS} words`
                  : `Max ${MAX_DESCRIPTION_WORDS} words`}
              </span>
            </label>
            <textarea
              id={descId}
              className="med3-textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(clampWords(e.target.value, MAX_DESCRIPTION_WORDS))}
              placeholder="Outcome notes — side effects, remaining stock…"
              disabled={submitting}
            />
          </div>

          {feedback && (
            <div className={`cc-feedback is-${feedback.kind}`}>{feedback.text}</div>
          )}

          <footer className="med3-foot">
            <button type="button" className="med3-btn med3-btn-sec" onClick={close}>
              {submitting ? "Close" : "Cancel"}
            </button>
            <button
              type="button"
              className="med3-btn med3-btn-pri"
              onClick={submit}
              disabled={!canSubmit}
              title={blockedBySibling ? "Another medicine is saving" : undefined}
            >
              {submitting ? "Saving…" : "Save review"}
            </button>
          </footer>
        </div>
      </div>
    </section>
  );
};

MedicineAdherenceForm.propTypes = {
  patientId: PropTypes.number,
  formId: PropTypes.number.isRequired,
  editing: PropTypes.object.isRequired,
  ordinal: PropTypes.number,
  showOrdinal: PropTypes.bool,
  blockedBySibling: PropTypes.bool,
  onFilledChange: PropTypes.func.isRequired,
  onSavingChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MedicineAdherenceForm;
